import { MuiButton } from '@/components';
import { UseFirstRender, UseIntermitence } from '@/hooks';
import { resetForgotPassword, resetPassword, resetPasswordView, validateResetCode } from '@/store/actions';
import { authSelector, intermitenceSelector } from '@/store/selectors';
import { Box, Grid, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikConfig } from './formik';
import MuiTextField3 from '@/components/TextField3';
import { palette } from '@/theme/constants';
import Image from 'next/image';

export const ChangePassword = (setLoading: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const { code, email } = router.query;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [token, setToken]: any = useState('');
  const { status: showPassword, switchStatus: switchShowPassword } = UseIntermitence();
  const { status: showConfirmPassword, switchStatus: switchShowConfirmPassword } = UseIntermitence();
  const { loading } = useSelector(intermitenceSelector);
  const authData: any = useSelector(authSelector);

  const sendToken = () => {
    if (authData?.recover?.message?.toLowerCase()?.includes('validated')) {
      dispatch(resetForgotPassword());
    }

    let emailData = authData?.email || email;
    dispatch(validateResetCode({ email: emailData, code: token }));
  };

  useEffect(() => {
    if (router.isReady && code) {
      return setToken(code);
    }
  }, []);

  useEffect(() => {
    if (token) {
      sendToken();
    }
  }, [token]);

  UseFirstRender(() => {
    dispatch(resetPasswordView());
  }, [dispatch]);

  const handleSubmit = (data: any) => {
    let email = authData?.email;
    let code = authData?.code;
    dispatch(resetPassword({ ...data, email, code }));
  };

  useEffect(() => {
    if (authData?.isAuth) router.push('/app/home');
  }, [authData?.isAuth]);

  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });

  const {
    values,
    handleSubmit: formikSubmit,
    handleChange,
    errors,
    touched,
    setTouched,
    dirty,
    isValid,
  } = FormikConfig(handleSubmit);

  const changeInputStatus = (value: string, error: any) => {
    if (value !== '') {
      if (error) return 'error';
      return 'inherit';
    }
    return 'inherit';
  };

  return (
    <Box
      display={'flex'}
      height={'100vh'}
      flexDirection={'column'}
      width={'100%'}
      zIndex={1}
      position={'relative'}
      padding={'0 1rem'}
      paddingTop={'46px'}
      justifyContent={!isMobile ? 'center' : ''}
      alignItems={'center'}>
      <IconButton
        disableRipple
        onClick={(e) => router.push('/')}
        sx={{ position: 'absolute', top: '1rem', left: isMobile ? '1rem' : '20rem' }}>
        <Image width={24} height={24} alt='back' color='black' src='/icons/left-arrow-black.svg' />
        {!isMobile && (
          <Typography fontSize={'1rem'} color='#212d6d' marginLeft={'0.6rem'}>
            Back to Memvy.com
          </Typography>
        )}
      </IconButton>
      {!isMobile && (
        <Image
          src={isMobile ? '/images/thread_mobile.svg' : '/images/thread.svg'}
          style={{
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            margin: 'auto',
            zIndex: -1,
          }}
          alt='thread'
          fill
          priority
          sizes='100%'
          quality={80}
        />
      )}

      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        width={'100%'}
        maxWidth={'29.5rem'}
        borderRadius={'0.5rem'}
        padding={isMobile ? '1rem' : '2rem'}>
        <Box>
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'} my={'1rem'}>
            <Image src={`/images/logo-new.svg`} alt={'logo'} width={66} height={34} quality={80} />
          </Box>
          <Typography
            marginBottom={'1.5rem'}
            color='#1D1D1D'
            align='center'
            fontSize={'1.8rem'}
            variant='h4'
            fontWeight={'400'}>
            {t('reset_password')}
          </Typography>

          <Typography marginBottom={'1.2rem'} color='#1D1D1D' fontSize={'1rem'} align='center' variant='body2'>
            {t('enter_new_password')}
          </Typography>

          <form onSubmit={formikSubmit}>
            <Grid container width={'100%'} marginBottom={'0.5rem'} rowSpacing={2}>
              <Grid item xs={12}>
                <Typography align='left' color={palette?.background} mb={'0.5rem'} fontSize={'1rem'}>
                  Password
                </Typography>
                <MuiTextField3
                  id='password'
                  name='password'
                  fullWidth
                  onBlur={() => {
                    handleOnTouched('password');
                  }}
                  onChange={handleChange}
                  value={values.password}
                  autoComplete='new-password'
                  placeholder={'Enter a password'}
                  iconMethod={switchShowPassword}
                  iconHeight={24}
                  iconWidth={24}
                  type={!showPassword ? 'password' : 'text'}
                  endIcon={showPassword ? '/icons/eye-blue.svg' : '/icons/eye-light.svg'}
                  errorMessage={errors.password}
                  status={changeInputStatus(values.password, errors.password && touched.password)}
                />
                <Typography align='left' color='#5C6372' mt={'6px'} fontSize={'0.8rem'}>
                  Must contain at least 10 characters.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography align='left' color={palette?.background} mb={'0.5rem'} fontSize={'1rem'}>
                  Confirm password
                </Typography>
                <MuiTextField3
                  id='confirm_password'
                  name='confirm_password'
                  fullWidth
                  onBlur={() => {
                    handleOnTouched('confirm_password');
                  }}
                  onChange={handleChange}
                  value={values.confirm_password}
                  autoComplete='new-password'
                  placeholder={'Enter a password'}
                  iconMethod={switchShowConfirmPassword}
                  iconHeight={24}
                  iconWidth={24}
                  type={!showConfirmPassword ? 'password' : 'text'}
                  endIcon={showConfirmPassword ? '/icons/eye-blue.svg' : '/icons/eye-light.svg'}
                  errorMessage={errors.confirm_password}
                  status={changeInputStatus(
                    values.confirm_password,
                    errors.confirm_password && touched.confirm_password,
                  )}
                />
                <Typography align='left' color='#5C6372' mt={'6px'} fontSize={'0.8rem'}>
                  Must contain at least 10 characters.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <MuiButton
                  type='submit'
                  disabled={!isValid || !dirty || loading}
                  loading={loading}
                  variant={'contained'}
                  sx={{
                    '&:disabled': {
                      backgroundColor: '#B0B3C0',
                      color: '#FFFFFF',
                      opacity: 1,
                    },
                    height: '60px',
                    borderRadius: '0.6rem',
                    fontSize: '1rem',
                    backgroundColor: !isValid ? '#B0B3C0' : '#212D6D',
                    mt: 2,
                  }}>
                  <Typography variant='button'>{t('reset_password')}</Typography>
                </MuiButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </Box>
  );
};
